/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@e2n/ui';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArbeitsbereichSelect } from '@/domain/dienstplan/shared/components/ArbeitsbereichSelect';
import { useArbeitsbereiche } from '@/domain/dienstplan/shared/hooks/useArbeitsbereiche/useArbeitsbereiche';
import { Weekday, WEEKDAYS } from '@/domain/dienstplan/shared/types/Weekday';
import { SettingsMindestbelegung } from '@/domain/dienstplan/features/SettingsMindestbelegung/SettingsMindestbelegung';
import { getLocaleWeekdayString } from '@/domain/dienstplan/shared/utils/getLocaleWeekdayString';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useBelegungen } from '@/domain/dienstplan/features/VisualEditor/hooks/useBelegungen/useBelegungen';
import { ErrorBoundary } from '@/components';
import { StackedBelegungenChart } from '@/domain/dienstplan/features/StackedChart/StackedBelegungenChart';
import { ErrorFallback } from '@/domain/dienstplan/features/ErrorFallback/ErrorFallback';
import { VisualEditorWrapper } from '@/domain/dienstplan/features/VisualEditor';

function validateRouteParam(weekday: string): boolean {
  const validWeekdays: Weekday[] = [
    'montag',
    'dienstag',
    'mittwoch',
    'donnerstag',
    'freitag',
    'samstag',
    'sonntag',
    'feiertag',
  ];
  return validWeekdays.includes(weekday as Weekday);
}

export function Page() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { weekday, arbeitsbereichId } = useParams();
  const arbeitsbereiche = useArbeitsbereiche(standortId);
  const [selectedArbeitsbereichId, setSelectedArbeitsbereichId] = useState<
    string | null
  >(arbeitsbereichId ?? null);

  useEffect(() => {
    setSelectedArbeitsbereichId(arbeitsbereichId ?? null);
  }, [arbeitsbereichId]);

  const belegungen = useBelegungen(
    validateRouteParam(weekday) ? (weekday as Weekday) : 'montag',
  );

  useEffect(() => {
    if (!weekday || !validateRouteParam(weekday)) {
      navigate(`/dienstplan/mindestbelegung/montag`, { replace: true });
    }
  }, [navigate, weekday]);

  return (
    <>
      <Helmet>
        <title>{t('domain.dienstplan.mindestbelegung.title')}</title>
      </Helmet>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col justify-start gap-3">
          <h2 className="font-extrabold text-2xl">
            {t('domain.dienstplan.mindestbelegung.title')}
          </h2>
          <div className="text-sm text-neutrals-800">
            {t('domain.dienstplan.mindestbelegung.subtitle')}
          </div>
        </div>
        <Tabs
          value={weekday}
          onValueChange={(value) => {
            if (selectedArbeitsbereichId) {
              navigate(
                `/dienstplan/mindestbelegung/${value}/${selectedArbeitsbereichId}`,
              );
            } else {
              navigate(`/dienstplan/mindestbelegung/${value}`);
            }
          }}>
          <div className="flex w-full justify-between">
            <TabsList className="bg-neutrals-300">
              {WEEKDAYS.map((day) => (
                <TabsTrigger key={day} value={day}>
                  {getLocaleWeekdayString(day as Weekday)}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex gap-2">
              <ArbeitsbereichSelect
                defaultArbeitsbereich={
                  arbeitsbereiche.find(
                    (ab) => ab.id === selectedArbeitsbereichId,
                  ) ?? null
                }
                arbeitsbereiche={arbeitsbereiche}
                onSelect={(arbeitsbereich) => {
                  if (!arbeitsbereich) {
                    setSelectedArbeitsbereichId(null);
                    navigate(`/dienstplan/mindestbelegung/${weekday}`, {
                      replace: true,
                    });
                    return;
                  }
                  setSelectedArbeitsbereichId(arbeitsbereich.id);
                  navigate(
                    `/dienstplan/mindestbelegung/${weekday}/${arbeitsbereich.id}`,
                    {
                      replace: true,
                    },
                  );
                }}
              />
              {selectedArbeitsbereichId && (
                <SettingsMindestbelegung
                  key={`${selectedArbeitsbereichId}-${weekday}`}
                  wochentag={weekday as Weekday}
                  arbeitsbereich={arbeitsbereiche.find(
                    (ab) => ab.id === selectedArbeitsbereichId,
                  )}
                />
              )}
            </div>
          </div>

          <TabsContent className="mt-8" value={weekday}>
            {selectedArbeitsbereichId && (
              <ErrorBoundary
                FallbackComponent={() => (
                  <ErrorFallback
                    message={t('domain.dienstplan.mindestbelegung.error')}
                  />
                )}>
                <VisualEditorWrapper
                  selectedArbeitsbereich={
                    arbeitsbereiche.find(
                      (ab) => ab.id === selectedArbeitsbereichId,
                    ) ?? null
                  }
                  wochentag={weekday as Weekday}
                  belegungen={belegungen}
                />
              </ErrorBoundary>
            )}
            {!selectedArbeitsbereichId && (
              <ErrorBoundary
                FallbackComponent={() => (
                  <ErrorFallback
                    message={t('domain.dienstplan.mindestbelegung.error')}
                  />
                )}>
                <StackedBelegungenChart belegungen={belegungen} />
              </ErrorBoundary>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
